/**
 * `<SlotCardMultiPick>` — checkbox-grid for the `multi-pick` slot type.
 *
 * Honours the slot's `multiPick: { min, max }` constraints by exposing
 * a pure `isMultiPickValid` helper for the parent's submit-disabled
 * matrix. The card itself never blocks selection — clicking an option
 * past `max` is permitted at the click level so the user can swap
 * picks without first deselecting; we surface the violation as a
 * helper hint and the parent's Submit gate enforces it.
 */

import type { SlotDefinition } from '@volley/crucible/interview/types.js';

export interface SlotCardMultiPickProps {
  slot: SlotDefinition;
  value: string[];
  onChange: (next: string[]) => void;
  prefilledUntouched: boolean;
}

/** Pure validator — true iff `selected.length` is within
 *  `[multiPick.min, multiPick.max]`. Exported so the parent
 *  `<SlotCard>`'s submit gate can call it without a DOM. */
export function isMultiPickValid(
  slot: SlotDefinition,
  selected: readonly string[],
): boolean {
  const cfg = slot.multiPick;
  if (!cfg) {
    // No constraints declared — any non-empty selection is valid.
    return selected.length > 0;
  }
  return selected.length >= cfg.min && selected.length <= cfg.max;
}

export function SlotCardMultiPick({
  slot,
  value,
  onChange,
  prefilledUntouched,
}: SlotCardMultiPickProps): React.JSX.Element {
  const options = slot.options ?? [];
  const cfg = slot.multiPick;

  const toggle = (opt: string): void => {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      onChange([...value, opt]);
    }
  };

  return (
    <div data-testid={`slot-card-multi-pick-${slot.id}`}>
      {cfg && (
        <p
          className="mb-2 text-[10px] uppercase tracking-wide text-white/40"
          data-testid={`slot-card-multi-pick-${slot.id}-constraint`}
        >
          Pick {cfg.min === cfg.max ? `exactly ${cfg.min}` : `${cfg.min}–${cfg.max}`}
        </p>
      )}
      <div className="grid gap-1.5">
        {options.map((opt) => {
          const selected = value.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              data-testid={`slot-card-multi-pick-${slot.id}-option`}
              data-option={opt}
              data-selected={selected ? 'true' : 'false'}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors ${
                selected
                  ? 'border-crucible-accent/60 bg-crucible-accent/10'
                  : 'border-white/10 bg-black/20 hover:border-crucible-accent/40 hover:bg-crucible-accent/10'
              } ${selected && prefilledUntouched ? 'opacity-60' : ''}`}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                  selected
                    ? 'border-crucible-accent bg-crucible-accent text-[10px] font-semibold text-white'
                    : 'border-white/20 bg-transparent'
                }`}
                aria-hidden="true"
              >
                {selected ? '✓' : ''}
              </span>
              <span
                className={`text-sm ${selected ? 'font-medium text-white' : 'text-white/80'}`}
              >
                {opt}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
